import useFormApi, { } from "../../api/FormApi";
import useKitApi, { KitPackage, KitPackageForm, KitPackageShape } from "../../api/KitApi";
import useShapeApi from "../../api/ShapeApi";
import Http from "../../engine/Http";
import { sortDimensions } from "../../model/Items";
import useAuthenticationHeader from "../../security/AuthenticationHeader.hook";

interface KitElement {
  name: string;
  length: number;
  width: number;
  depth: number;
  material: string;
  quantity: number;
}

const convertKitElement = (kitElement: any, dimension: any, quantity: number) : KitElement => ({
  name: kitElement.name,
  length: dimension.length,
  width: dimension.width,
  depth: dimension.depth,
  material: kitElement.material.name,
  quantity
});

const getDimension = (kitElement: KitElement) => sortDimensions(kitElement).join('x');

const findByDimensionsAndMaterial = (elements: any[], dimensions: any, material: any) =>
  elements.find(element => element.dimensions === dimensions && element.material === material);

const totalUpByDimensionsAndMaterial = (elements: any[]) =>
  elements.reduce((previous: any[], current: any) => {
    const matchingElement = findByDimensionsAndMaterial(previous, current.dimensions, current.material);
    if (matchingElement) {
      matchingElement.quantity += current.quantity;
    } else {
      previous.push(current);
    }
    return previous;
  }, []);

const fetchPackageForms = async (packageForms: KitPackageForm[], kitForms: any[]) => {
  const forms: any[] = [];
  for (const packageForm of packageForms) {
    if (packageForm.quantity > 0) {
      const packageFormId = packageForm.form.id;
      const kitForm = kitForms.find(kitForm => kitForm.id === packageFormId) as KitElement;
      if (kitForm === undefined) {
        console.warn(`Kit form not found to enrich packaging printout: ${packageFormId}`);
      } else {
        forms.push({
          name: kitForm.name,
          material: kitForm.material,
          dimensions: getDimension(kitForm),
          quantity: packageForm.quantity,
        });
      }
    }
  }
  return totalUpByDimensionsAndMaterial(forms);
};

const fetchPackageShapes = async (packageShapes: KitPackageShape[], kitShapes: any[]) => {
  const shapes: any[] = [];
  for (const packageShape of packageShapes) {
    if (packageShape.quantity > 0) {
      const packageShapeId = packageShape.shape.id;
      const kitShape = kitShapes.find(kitShape => kitShape.id === packageShapeId) as KitElement;
      if (kitShape === undefined) {
        console.warn(`Kit shape not found to enrich packaging printout: ${packageShapeId}`);
      } else {
        shapes.push({
          name: kitShape.name,
          material: kitShape.material,
          dimensions: getDimension(kitShape),
          quantity: packageShape.quantity
        });
      }
    }
  }
  return totalUpByDimensionsAndMaterial(shapes);
};

const convertPackage = async (kitPackage: KitPackage, kitForms: any[], kitShapes: any[]) => {
  const forms = await fetchPackageForms(kitPackage.packageForms, kitForms);
  const shapes = await fetchPackageShapes(kitPackage.packageShapes, kitShapes);
  return {
    name: kitPackage.name,
    packagingType: kitPackage.packagingType,
    packageForms: forms,
    packageShapes: shapes
  }
};

const useKitsPrintout = () => {
  const formApi = useFormApi();
  const shapeApi = useShapeApi();
  const kitApi = useKitApi();
  const authenticationHeader = useAuthenticationHeader();

  const convertForm = async (customerId: number, priceListId: number, kitForm: any) => {
    const form = await formApi.fetchForm(customerId, priceListId, kitForm.form.id);
    return convertKitElement(kitForm.form, form.dimension, kitForm.quantity);
  };

  const convertShape = async (customerId: number,priceListId: number, kitShape: any) => {
    const shape = await shapeApi.fetchShape(customerId, priceListId, kitShape.shape.id);
    return convertKitElement(kitShape.shape, shape.block, kitShape.quantity);
  };

  const fetchForms = async (customerId: number, priceListId: number,kitId: number) => {
    const kitForms = await kitApi.fetchKitForms(customerId, priceListId, kitId);
    const forms: any[] = [];
    for (const kitForm of kitForms) {
      const form = await convertForm(customerId, priceListId, kitForm);
      forms.push({ ...form, id: kitForm.form.id });
    }
    return forms;
  };

  const fetchShapes = async (customerId: number, priceListId: number,kitId: number) => {
    const kitShapes = await kitApi.fetchKitShapes(customerId, priceListId, kitId);
    const shapes: any[] = [];
    for (const kitShape of kitShapes) {
      const shape = await convertShape(customerId, priceListId, kitShape);
      shapes.push({ ...shape, id: kitShape.shape.id });
    }
    return shapes;
  };

  const getKitsForPrintout = async (customerId: number, priceListId: number, kitIds: number[]) => {
    const kitsResource = `customers/${customerId}/price-lists/${priceListId}/kits`;
      const kits: any[] = [];
      for (const kitId of kitIds) {
        const kit = await Http.get(`/api/${kitsResource}/${kitId}`, [ authenticationHeader ]);
        const forms = await fetchForms(customerId, priceListId, kitId);
        const shapes = await fetchShapes(customerId, priceListId, kitId);
        const kitPackages = await fetchPackages(customerId, kitId, forms, shapes)
        kits.push({
          name: kit.name,
          packagingType: kit.packagingType,
          elementCount: forms.length + shapes.length,
          forms,
          shapes,
          kitPackages
        });
      }
      console.info(`Kits: ${kits}`);
      return kits;
  };

  const fetchPackages = async (customerId: number, kitId: number, forms: any[], shapes: any[]) => {
    const kitPackages = await kitApi.fetchKitPackages(customerId, kitId);
    const packages: any[] = [];
    for (const kitPackage of kitPackages) {
      const convertedPackage = await convertPackage(kitPackage, forms, shapes);
      packages.push(convertedPackage);
    }
    packages.sort((p, r) => p.name.localeCompare(r.name));
    return packages;
  };

  return {
    getKitsForPrintout
  }
};

export default useKitsPrintout;